import React, { useState } from 'react';
import {
    Camera,
    ChevronLeft,
    Upload,
    CheckCircle2,
    AlertCircle,
    FileImage,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { cn } from '@/shared/lib/utils';
import { uploadKYCImage } from '@/shared/utils/uploadImage';

const SelfieVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { submitApplication } = useKYC(user?.uid);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!selectedFile) return;

        setIsSubmitting(true);
        try {
            // Upload image to Firebase Storage (60초 타임아웃)
            const uploadTimeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('업로드 시간이 초과되었습니다. 네트워크를 확인하고 다시 시도해주세요.')), 60000)
            );

            const imageUrl = await Promise.race([
                uploadKYCImage(selectedFile, user!.uid, 'SELFIE'),
                uploadTimeout
            ]);

            // Submit application with real image URL
            const result = await submitApplication('SELFIE', {
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type,
                imageUrl: imageUrl
            });

            if (result.success) {
                alert('자산 보호를 위한 KYC 본인 인증 단계입니다. AI 분석을 통해 제출하신 자료의 무결성을 검토 중입니다. 최종 승인 단계까지는 5~10분이 소요됩니다.');
                navigate('/verification');
            } else {
                alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            const msg = error instanceof Error ? error.message : '이미지 업로드 중 오류가 발생했습니다.';
            alert(msg + '\n다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-white flex items-center gap-3">
                    <Camera className="h-8 w-8 text-pink-500" /> 셀카 인증
                </h1>
                <p className="text-slate-500 font-bold text-sm">본인의 얼굴이 잘 나온 사진을 업로드해주세요.</p>
            </div>

            <div className="rounded-[2.5rem] bg-[#0f0f12] border border-white/5 p-8 shadow-2xl space-y-8">
                <div className="space-y-6">
                    <div className="relative group">
                        <input
                            type="file"
                            accept="image/*"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    setSelectedFile(e.target.files[0]);
                                }
                            }}
                        />
                        <div className={cn(
                            "h-64 border-2 border-dashed rounded-3xl flex flex-col items-center justify-center gap-4 transition-all duration-300",
                            selectedFile ? "border-pink-500/50 bg-pink-500/5" : "border-white/5 bg-white/[0.02] group-hover:border-pink-500/30 group-hover:bg-pink-500/5"
                        )}>
                            {selectedFile ? (
                                <>
                                    <div className="w-16 h-16 rounded-2xl bg-pink-500 flex items-center justify-center text-white shadow-lg shadow-pink-500/20">
                                        <CheckCircle2 className="h-8 w-8" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm font-black text-white">{selectedFile.name}</p>
                                        <p className="text-[10px] font-bold text-pink-500 mt-1 uppercase">업로드 준비 완료</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform">
                                        <Upload className="h-8 w-8" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm font-black text-white px-8">사진 촬영 또는 업로드</p>
                                        <p className="text-[10px] font-bold text-slate-500 mt-1 uppercase tracking-widest">JPG, PNG (MAX 10MB)</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 rounded-3xl bg-pink-500/5 border border-pink-500/10 flex items-start gap-4">
                    <AlertCircle className="h-5 w-5 text-pink-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-pink-500">인증 가이드</p>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                            신분증과 대조하여 본인 여부를 확인합니다. 모자나 선글라스를 벗고 정면을 응시해주세요.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={!selectedFile || isSubmitting}
                    className="w-full h-16 rounded-2xl bg-pink-600 text-white font-black shadow-xl shadow-pink-600/20 hover:bg-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3"
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "셀카 제출하기"}
                </button>
            </div>
        </div>
    );
};

export default SelfieVerificationPage;
